/**
 * Typewriter effect for terminal cards.
 *
 * Supports multiple elements per page — each [data-typewriter]
 * element animates independently with its own state. Per-page
 * seen tracking ensures revisiting a page shows text immediately
 * while new pages play the animation fresh.
 *
 * Accessibility:
 *   - prefers-reduced-motion: shows text immediately, no animation
 *   - Screen readers receive full text via sr-only element (HTML contract)
 *   - Animated element has aria-hidden="true" (HTML contract)
 *
 * Cursor:
 *   - Underscore cursor (_) matches the landing page cursor
 *   - After typing completes, cursor moves to its own line and blinks
 *     indefinitely via CSS animation (never fades out)
 *
 * Preserves HTML tags (like neon accent spans) by treating them
 * as invisible — tags are inserted instantly, only visible
 * characters are typed one at a time.
 */

const SEEN_PREFIX = 'typewriter:seen:'
const BASE_SPEED = 23       // ~20-30ms per character (fast and snappy)
const VARIATION = 15         // +/- 15ms random variation (organic feel)

export function initTypewriter() {
  const elements = document.querySelectorAll('[data-typewriter]')
  if (elements.length === 0) return

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches

  elements.forEach(function (card) {
    const pageKey = card.dataset.typewriterPage || window.location.pathname
    const seenKey = SEEN_PREFIX + pageKey
    const seen = localStorage.getItem(seenKey)

    if (seen || prefersReducedMotion) {
      showImmediate(card)
      return
    }

    // Use IntersectionObserver to trigger animation when near viewport
    observeAndAnimate(card, seenKey)
  })
}

/**
 * Show all text immediately without animation.
 * For returning visitors or reduced-motion users.
 * Cursor is placed in its done position (own line, blinking indefinitely).
 */
function showImmediate(card) {
  const body = card.querySelector('.terminal-body')
  const cursor = card.querySelector('.terminal-cursor')
  if (!body) return

  // Text is already in the HTML from server render — just ensure paragraphs are visible
  const paragraphs = body.querySelectorAll('p')
  paragraphs.forEach(function (p) { p.style.display = '' })

  // Place cursor in done position — blinking indefinitely on its own line
  if (cursor) {
    card.appendChild(cursor)
    cursor.classList.add('terminal-cursor--done')
  }

  // Apply random neon keyword coloring to summary cards
  highlightKeywords(card)

  // Mark element as ready (prevents FOUC — see CSS contract)
  card.dataset.typewriterReady = 'true'
}

/**
 * Use IntersectionObserver to trigger animation when element
 * is near viewport. Pattern from comments-lazy-load.js.
 */
function observeAndAnimate(card, seenKey) {
  if ('IntersectionObserver' in window) {
    const observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          animateTypewriter(card, seenKey)
          observer.unobserve(card)
        }
      })
    }, { rootMargin: '100px' })

    observer.observe(card)
  } else {
    // Fallback: animate immediately
    animateTypewriter(card, seenKey)
  }
}

/**
 * Core animation with per-element state (no shared globals).
 * Each call creates its own closure with independent entryIndex/charIndex.
 */
function animateTypewriter(card, seenKey) {
  const body = card.querySelector('.terminal-body')
  const cursor = card.querySelector('.terminal-cursor')
  if (!body || !cursor) return

  const paragraphs = body.querySelectorAll('p')
  if (paragraphs.length === 0) return

  // Mark as ready (JS has taken control)
  card.dataset.typewriterReady = 'true'

  // Apply neon keyword highlighting BEFORE capturing innerHTML
  // so neon spans are baked into fullHTML and visible during typing
  highlightKeywords(card)

  // Capture full innerHTML per paragraph, then clear for typing
  const entries = []
  paragraphs.forEach(function (p) {
    entries.push({ node: p, fullHTML: p.innerHTML })
    p.innerHTML = ''
    p.style.display = 'none'
  })

  // Show first paragraph and position cursor
  entries[0].node.style.display = ''
  entries[0].node.appendChild(cursor)
  cursor.style.display = ''

  // Per-instance state (no shared globals — TYPE-01 requirement)
  let entryIndex = 0
  let charIndex = 0

  function type() {
    if (entryIndex >= entries.length) {
      // Typing complete — move cursor to done position
      finishTyping(card, cursor, seenKey)
      return
    }

    const entry = entries[entryIndex]
    const totalVisible = visibleLength(entry.fullHTML)
    charIndex++

    // Set partial HTML + re-append cursor
    entry.node.innerHTML = htmlSlice(entry.fullHTML, charIndex)
    entry.node.appendChild(cursor)

    if (charIndex >= totalVisible) {
      // Restore full HTML for completed paragraph
      entry.node.innerHTML = entry.fullHTML
      entryIndex++
      charIndex = 0
      if (entryIndex < entries.length) {
        entries[entryIndex].node.style.display = ''
        entries[entryIndex].node.appendChild(cursor)
      }
    }

    // Random speed variation: BASE_SPEED +/- VARIATION (organic feel)
    const speed = BASE_SPEED + (Math.random() * VARIATION * 2 - VARIATION)
    requestAnimationFrame(function () { setTimeout(type, speed) })
  }

  // Start typing after brief delay
  setTimeout(type, 400)
}

/**
 * After typing completes: cursor moves to its own line and blinks
 * indefinitely via CSS animation (terminal-blink). Marks page as seen.
 */
function finishTyping(card, cursor, seenKey) {
  // Move cursor to its own line (done state)
  card.appendChild(cursor)
  cursor.classList.add('terminal-cursor--done')

  // Mark as seen in localStorage
  localStorage.setItem(seenKey, 'true')
}

/**
 * Apply neon keyword coloring to summary card text.
 * Only applies to `.terminal-card--summary` elements. The landing page
 * (`home.liquid`) also uses this class, so it IS processed — regex
 * lookbehind prevents double-wrapping of any hand-crafted neon spans.
 *
 * Called BEFORE animateTypewriter captures innerHTML, so neon-colored
 * words are visible during typing animation (not applied after).
 * Also called in showImmediate for returning visitors.
 *
 * Ruby and Rails are always highlighted in neon-pink. General keywords
 * are selected by content relevance: tech terms first, then proper nouns,
 * then remaining significant words.
 *
 * Uses a seeded random based on the page URL so the same page
 * always gets the same highlighted words.
 */
function highlightKeywords(card) {
  if (!card.classList.contains('terminal-card--summary')) return

  const body = card.querySelector('.terminal-body')
  if (!body) return

  const neonClasses = ['neon-pink', 'neon-cyan', 'neon-teal', 'neon-purple']
  const stopWords = new Set([
    'the', 'and', 'with', 'that', 'this', 'from', 'have', 'been',
    'more', 'when', 'will', 'into', 'also', 'each', 'they', 'were',
    'what', 'your', 'about', 'than', 'then', 'just', 'like', 'over',
    'some', 'very', 'only', 'most', 'such', 'where', 'does', 'should',
    'would', 'could', 'which', 'their', 'there', 'these', 'those', 'being',
    'other', 'after', 'before', 'through', 'between', 'while', 'using',
    'under', 'along', 'every', 'still'
  ])

  // Words that are ALWAYS highlighted in neon-pink (Ruby/Rails priority)
  var alwaysHighlight = [
    { word: 'Ruby', cls: 'neon-pink' },
    { word: 'Rails', cls: 'neon-pink' }
  ]
  var alreadyHighlighted = new Set(alwaysHighlight.map(function (h) { return h.word }))

  // Content-relevant tech terms that make good highlight candidates
  var techTerms = new Set([
    'API', 'CLI', 'HTML', 'CSS', 'JSON', 'YAML', 'HTTP', 'SQL', 'Git',
    'Docker', 'Kubernetes', 'React', 'Vue', 'Node', 'Python', 'JavaScript',
    'TypeScript', 'Rust', 'Elixir', 'Phoenix', 'Django', 'Flask', 'Express',
    'Redis', 'PostgreSQL', 'MongoDB', 'GraphQL', 'REST', 'AWS', 'Azure',
    'Linux', 'Nginx', 'Terraform', 'Ansible', 'Webpack', 'Bridgetown',
    'Jekyll', 'Tailwind', 'esbuild', 'Cloudflare', 'Heroku', 'Vercel',
    'Netlify', 'DevOps', 'SRE', 'microservices', 'monolith', 'refactor',
    'deploy', 'pipeline', 'middleware', 'endpoint', 'schema', 'migration',
    'framework', 'library', 'algorithm', 'architecture', 'infrastructure',
    'container', 'cluster', 'serverless', 'lambda', 'queue', 'cache',
    'database', 'compiler', 'runtime', 'debugger', 'profiler',
    'Swift', 'Kotlin', 'Java', 'TDD', 'OOP', 'MVC', 'SOLID',
    'Agile', 'Scrum', 'Kanban'
  ])

  // Cyberpunk and poetic vocabulary that evokes the landing page tone
  var cyberTerms = new Set([
    'neon', 'cyber', 'digital', 'neural', 'chrome', 'ghost', 'matrix',
    'signal', 'protocol', 'daemon', 'kernel', 'terminal', 'node', 'grid',
    'hack', 'glitch', 'static', 'entropy', 'flux', 'quantum', 'synth',
    'hologram', 'void', 'shadow', 'pulse', 'circuit', 'interface', 'nexus',
    'core', 'mesh', 'vector', 'synthetic', 'memory', 'data', 'code',
    'system', 'virtual', 'binary', 'stream', 'echo', 'noise', 'dream',
    'chaos', 'drift', 'trace', 'loop', 'stack', 'thread', 'process', 'sync',
    'power', 'wave', 'line', 'edge', 'port', 'patch', 'probe', 'scan',
    'mode', 'init', 'boot', 'link', 'ping', 'break', 'overflow', 'crash'
  ])

  // Simple string hash for seeded random (consistent per page)
  var pageKey = card.dataset.typewriterPage || window.location.pathname
  var seed = 0
  for (var i = 0; i < pageKey.length; i++) {
    seed = ((seed << 5) - seed + pageKey.charCodeAt(i)) | 0
  }
  function seededRandom() {
    seed = (seed * 1664525 + 1013904223) & 0x7fffffff
    return seed / 0x7fffffff
  }

  var paragraphs = body.querySelectorAll('p')
  paragraphs.forEach(function (p) {
    var html = p.innerHTML

    // Step 1: Always highlight Ruby and Rails in neon-pink
    alwaysHighlight.forEach(function (entry) {
      var regex = new RegExp('\\b(' + entry.word + ')\\b', 'g')
      html = html.replace(regex, '<span class="' + entry.cls + '">$1</span>')
    })

    // Step 2: Find candidate words for general highlighting
    var text = p.textContent
    var words = text.match(/\b[a-zA-Z]{4,}\b/g)
    if (!words) {
      p.innerHTML = html
      return
    }

    // Deduplicate and filter out stop words and already-highlighted words
    var seen = new Set()
    var candidates = []
    words.forEach(function (w) {
      if (!seen.has(w) && !stopWords.has(w.toLowerCase()) && !alreadyHighlighted.has(w)) {
        seen.add(w)
        candidates.push(w)
      }
    })

    if (candidates.length > 0) {
      // Score candidates by relevance: tech terms > capitalized > others
      var scored = candidates.map(function (w) {
        var score = 0
        if (techTerms.has(w) || cyberTerms.has(w)) score += 10
        if (w[0] === w[0].toUpperCase() && w[0] !== w[0].toLowerCase()) score += 5
        return { word: w, score: score }
      })

      // Sort by score descending, then shuffle within same score for variety
      scored.sort(function (a, b) {
        if (b.score !== a.score) return b.score - a.score
        return seededRandom() - 0.5
      })

      // Pick 1-3 keywords per paragraph
      var pickCount = Math.min(scored.length, 1 + Math.floor(seededRandom() * 3))
      var picks = scored.slice(0, pickCount)

      // Wrap each picked word with a neon span (first occurrence only,
      // but skip matches already inside existing <span> tags)
      picks.forEach(function (pick) {
        var cls = neonClasses[Math.floor(seededRandom() * neonClasses.length)]
        // Match whole word NOT preceded by "> (which would indicate it is
        // already inside a span tag from alwaysHighlight)
        var regex = new RegExp('(?<![">])\\b(' + pick.word + ')\\b(?![^<]*<\\/span>)')
        html = html.replace(regex, '<span class="' + cls + '">$1</span>')
      })
    }

    p.innerHTML = html
  })
}

/**
 * Build a substring of HTML that shows exactly `count`
 * visible characters. Tags are included whole (they're
 * invisible) and don't count toward the character limit.
 */
function htmlSlice(html, count) {
  let visible = 0
  let i = 0
  while (i < html.length && visible < count) {
    if (html[i] === '<') {
      // Skip the entire tag
      const closeIdx = html.indexOf('>', i)
      if (closeIdx !== -1) {
        i = closeIdx + 1
      } else {
        i++
      }
    } else if (html[i] === '&') {
      // HTML entity — counts as one visible char
      const semiIdx = html.indexOf(';', i)
      if (semiIdx !== -1 && semiIdx - i < 10) {
        i = semiIdx + 1
      } else {
        i++
      }
      visible++
    } else {
      i++
      visible++
    }
  }
  // Include any trailing closing tags so the HTML is valid
  while (i < html.length && html[i] === '<') {
    const closeIdx = html.indexOf('>', i)
    if (closeIdx !== -1) {
      i = closeIdx + 1
    } else {
      break
    }
  }
  return html.slice(0, i)
}

/** Count visible characters in HTML string */
function visibleLength(html) {
  let count = 0
  let i = 0
  while (i < html.length) {
    if (html[i] === '<') {
      const closeIdx = html.indexOf('>', i)
      i = closeIdx !== -1 ? closeIdx + 1 : i + 1
    } else if (html[i] === '&') {
      const semiIdx = html.indexOf(';', i)
      if (semiIdx !== -1 && semiIdx - i < 10) {
        i = semiIdx + 1
      } else {
        i++
      }
      count++
    } else {
      i++
      count++
    }
  }
  return count
}
